import _ from "lodash";
import deepmerge from "deepmerge";

import { parseJsonStyleObject } from "./labelsStylesheetUtils";

export const HIERARCHICAL_STYLE_PROPS = ["fontFamily", "color", "fontWeight", "fontStyle", "fontSize", "textAlign", "lineHeight"];
export const DESCENDANT_ITEM_STYLE_OVERRIDE_KEYS = ["item", "labels", "label", "label1", "label2", "choiceItem", "choiceLabel", "choiceControl", "choiceContainer", "choiceCaption"];

const LOCAL_DIRECT_STYLE_PROPS = [
	"backgroundColor",
	"borderColor",
	"color",
	"iconColor",
	"label2Color",
	"labelOnlyBackgroundColor"
];

const LAYOUT_PROPERTY_DEFINITIONS = [
	{ name: "defaultView", label: "Default View", defaultValue: "checklistView" },
	{ name: "procedureViewType", label: "View Type", defaultValue: "default" },
	{ name: "itemHeight", label: "Item Height", defaultValue: "default", anchor: "itemLayout" },
	{ name: "fullscreenStartMode", label: "Fullscreen Start", defaultValue: "userPreference" },
	{ name: "fullscreenSuppressHideLabelsToggle", label: "Suppress Hide Labels Toggle", defaultValue: false },
	{ name: "fullscreenTransition", label: "Fullscreen Transition", defaultValue: "none" },
	{ name: "fullscreenTransitionIn", label: "Transition In", defaultValue: "" },
	{ name: "fullscreenTransitionOut", label: "Transition Out", defaultValue: "" },
	{ name: "fullscreenTransitionDelay", label: "Transition Delay", defaultValue: "none" },
	{ name: "fullscreenActionVariant", label: "Action Variant", defaultValue: "standard", anchor: "layoutAdvanced" },
	{ name: "fullscreenActionPlacement", label: "Action Placement", defaultValue: "overlay", anchor: "layoutAdvanced" },
	{ name: "fullscreenActionFlowWhenScrollable", label: "Scrollable Action Flow", defaultValue: false, anchor: "layoutAdvanced" },
	{ name: "fullscreenSwipeNavigation", label: "Swipe Navigation", defaultValue: "enabled", anchor: "layoutAdvanced" },
	{ name: "fullscreenSwipeSound", label: "Swipe Sound", defaultValue: "auto", anchor: "layoutAdvanced" },
	{ name: "fullscreenLabelHideControl", label: "Label Hide Control", defaultValue: "auto", anchor: "layoutAdvanced" },
	{ name: "fullscreenActionNavAlignment", label: "Action Alignment", defaultValue: "left", anchor: "layoutAdvanced" },
	{ name: "fullscreenActionNavOrder", label: "Action Order", defaultValue: "primary-first", anchor: "layoutAdvanced" },
	{ name: "fullscreenActionStyle", label: "Action Style", defaultValue: "emphasized", anchor: "layoutAdvanced" },
	{ name: "fullscreenActionPrimaryColor", label: "Action Primary Color", defaultValue: "#2f6df6", anchor: "layoutAdvanced" },
	{ name: "fullscreenActionSurfaceColor", label: "Action Surface Color", defaultValue: "rgba(31,35,47,0.88)", anchor: "layoutAdvanced" },
	{ name: "containerKind", label: "Container Kind", defaultValue: "form", anchor: "containerLayout" },
	{ name: "layout", label: "Layout", defaultValue: "stack" },
	{ name: "printLayout", label: "Print Layout", defaultValue: "stack", anchor: "containerLayout" },
	{ name: "responsive", label: "Responsive Layout", defaultValue: {}, anchor: "containerLayout" },
	{ name: "showNestedBorder", label: "Nested Border", defaultValue: true, anchor: "containerLayout" },
	{ name: "size", label: "Choice Size", defaultValue: null, anchor: "choiceLayout" },
	{ source: "authorProperties", name: "sectionInitialExpansion", label: "Section Expansion", defaultValue: "selected" },
	{ source: "authorProperties", name: "sectionExpandCollapseControls", label: "Expand/Collapse Controls", defaultValue: "show" },
	{ source: "authorProperties", name: "procedurePlaceholderMode", label: "Placeholder Mode", defaultValue: "auto" },
	{ source: "authorProperties", name: "fullscreenSnapScroll", label: "Snap Scroll", defaultValue: false },
	{ source: "authorProperties", name: "fullscreenDisableScroll", label: "Disable Scroll", defaultValue: false },
	{ source: "authorProperties", name: "videoCardPreviewMode", label: "Video Card Preview", defaultValue: "pausedVideo" }
];

const MEDIA_CONTROLS_PROPERTY_DEFINITIONS = [
	{ name: "mediaFullscreenControlVisibility", label: "Fullscreen Control", defaultValue: "visible" },
	{ name: "mediaResizeToggleVisibility", label: "Resize Toggle", defaultValue: "hidden" },
	{ name: "mediaReplayControlVisibility", label: "Replay Control", defaultValue: "visible" }
];

const VIDEO_PROPERTY_DEFINITIONS = [
	{ name: "videoMute", label: "Mute", defaultValue: true },
	{ name: "videoLoop", label: "Loop", defaultValue: true },
	{ name: "videoShowInBackground", label: "Show In Background", defaultValue: false },
	{ name: "videoFitAvailableSpace", label: "Fit Available Space", defaultValue: false },
	{ name: "videoResizeMode", label: "Resize Mode", defaultValue: "contain" },
	{ name: "videoOverlayColor", label: "Overlay Color", defaultValue: "transparent" },
	{ name: "videoOverlayColorCustom", label: "Custom Overlay Color", defaultValue: "rgba(255,255,255,0.7)" },
	{ name: "videoBackgroundColor", label: "Background Color", defaultValue: "transparent" },
	{ name: "videoAspectRatio", label: "Aspect Ratio", defaultValue: "16:9" },
	{ name: "videoMaximumHeight", label: "Maximum Height", defaultValue: "default" },
	{ name: "videoMaximumHeightAnchor", label: "Vertical Anchor", defaultValue: "top" }
];

const IMAGE_PROPERTY_DEFINITIONS = [
	{ name: "imageShowInBackground", label: "Show In Background", defaultValue: false },
	{ name: "imageFitAvailableSpace", label: "Fit Available Space", defaultValue: false },
	{ name: "imageResizeMode", label: "Resize Mode", defaultValue: "contain" },
	{ name: "imageOverlayColor", label: "Overlay Color", defaultValue: "transparent" },
	{ name: "imageOverlayColorCustom", label: "Custom Overlay Color", defaultValue: "rgba(255,255,255,0.7)" },
	{ name: "imageAspectRatio", label: "Aspect Ratio", defaultValue: "16:9" },
	{ name: "imageMaximumHeight", label: "Maximum Height", defaultValue: "default" },
	{ name: "imageMaximumHeightAnchor", label: "Vertical Anchor", defaultValue: "top" },
	{ name: "backgroundMediaBlurFill", label: "Blur Fill", defaultValue: false }
];

export const INHERITED_PROPERTY_GROUPS = [
	{ key: "layout", label: "Layout", className: "layout-info", icon: "ruler-combined", anchor: "layout", properties: LAYOUT_PROPERTY_DEFINITIONS },
	{ key: "mediaControls", label: "Media Controls", className: "media-controls-info", icon: "tv", anchor: "mediaControls", properties: MEDIA_CONTROLS_PROPERTY_DEFINITIONS },
	{ key: "imageOptions", label: "Image Options", className: "image-options-info", icon: "image", anchor: "imageOptions", properties: IMAGE_PROPERTY_DEFINITIONS },
	{ key: "videoOptions", label: "Video Options", className: "video-options-info", icon: "video", anchor: "videoOptions", properties: VIDEO_PROPERTY_DEFINITIONS }
];

export function parseMaybeJsonObject(value, fallback = {}) {
	if (_.isPlainObject(value)) {
		return _.cloneDeep(value);
	}
	if (_.isString(value) && value.trim() !== "") {
		try {
			const parsed = JSON.parse(value);
			return _.isPlainObject(parsed) ? parsed : fallback;
		} catch (e) {
			return fallback;
		}
	}
	return fallback;
}

function normalizeComparableObject(value) {
	if (_.isArray(value)) {
		return value.map(normalizeComparableObject);
	}
	if (_.isPlainObject(value)) {
		return _.sortBy(Object.keys(value)).reduce((result, key) => {
			result[key] = normalizeComparableObject(value[key]);
			return result;
		}, {});
	}
	return value;
}

function normalizeComparableValue(value) {
	if (_.isPlainObject(value)) {
		return JSON.stringify(normalizeComparableObject(value));
	}
	if (_.isArray(value)) {
		return JSON.stringify(normalizeComparableObject(value));
	}
	if (_.isBoolean(value)) {
		return value ? "true" : "false";
	}
	if (_.isNumber(value)) {
		return `${value}`;
	}
	if (_.isString(value)) {
		return _.toLower(value.trim());
	}
	if (_.isNil(value)) {
		return "";
	}
	return _.toLower(`${value}`.trim());
}

function isUnsetOrInheritedValue(value) {
	if (_.isNil(value)) {
		return true;
	}
	if (_.isString(value)) {
		const normalized = _.toLower(value.trim());
		return normalized === "" || normalized === "inherit" || normalized === "{}" || normalized === "[]";
	}
	if (_.isPlainObject(value) || _.isArray(value)) {
		return _.isEmpty(value);
	}
	return false;
}

function getPropertyContainer(entity = {}, definition = {}) {
	if (definition.source === "authorProperties") {
		return parseMaybeJsonObject(entity.authorProperties, {});
	}
	return entity || {};
}

function getLocalPropertyValue(entity = {}, definition = {}) {
	const container = getPropertyContainer(entity, definition);
	if (!_.isPlainObject(container) || !Object.prototype.hasOwnProperty.call(container, definition.name)) {
		return {
			hasValue: false,
			value: undefined,
			normalizedValue: ""
		};
	}

	const value = container[definition.name];
	if (isUnsetOrInheritedValue(value)) {
		return {
			hasValue: false,
			value,
			normalizedValue: ""
		};
	}

	return {
		hasValue: true,
		value,
		normalizedValue: normalizeComparableValue(value)
	};
}

function getDefinitionDefaultValue(definition = {}) {
	return Object.prototype.hasOwnProperty.call(definition, "defaultValue") ? definition.defaultValue : undefined;
}

function getEffectiveAncestorPropertyValue(ancestorEntities = [], definition = {}) {
	for (let index = ancestorEntities.length - 1; index >= 0; index -= 1) {
		const localValue = getLocalPropertyValue(ancestorEntities[index], definition);
		if (localValue.hasValue) {
			return localValue.normalizedValue;
		}
	}

	const defaultValue = getDefinitionDefaultValue(definition);
	return isUnsetOrInheritedValue(defaultValue) ? "" : normalizeComparableValue(defaultValue);
}

function getOverrideAnchor(entity = {}, group = {}, definition = {}) {
	if (group.key === "layout" && definition.name === "layout") {
		const entityType = _.get(entity, "type", "");
		if (_.startsWith(entityType, "itemChoice")) {
			return "choiceLayout";
		}
		if (entityType === "itemContainer") {
			return "containerLayout";
		}
	}

	return definition.anchor || group.anchor || null;
}

function getAncestorEntitiesForTreeContext(rootNode = null, treePath = []) {
	if (!rootNode || !_.isArray(treePath)) {
		return null;
	}

	const chain = getNodeChainForTreePath(rootNode, treePath).map(getEntityFromNode);
	if (chain.length <= 1) {
		return [];
	}

	return chain.slice(0, -1);
}

function getLocalInheritedPropertyOverridesForGroup(entity = {}, group = {}, ancestorEntities = null) {
	return group.properties
		.map((definition) => {
			const localValue = getLocalPropertyValue(entity, definition);
			if (!localValue.hasValue) {
				return null;
			}

			const comparisonValue = _.isArray(ancestorEntities)
				? getEffectiveAncestorPropertyValue(ancestorEntities, definition)
				: (() => {
					const defaultValue = getDefinitionDefaultValue(definition);
					return isUnsetOrInheritedValue(defaultValue) ? "" : normalizeComparableValue(defaultValue);
				})();

			if (comparisonValue !== "" && localValue.normalizedValue === comparisonValue) {
				return null;
			}

			return {
				name: definition.name,
				label: definition.label || _.startCase(definition.name),
				value: localValue.value,
				anchor: getOverrideAnchor(entity, group, definition)
			};
		})
		.filter(Boolean);
}

export function getLocalInheritedPropertySummaries(entity = {}, options = {}) {
	const ancestorEntities = getAncestorEntitiesForTreeContext(options.rootNode, options.treePath);

	return INHERITED_PROPERTY_GROUPS
		.map((group) => {
			const overrides = getLocalInheritedPropertyOverridesForGroup(entity, group, ancestorEntities);
			return {
				key: group.key,
				label: group.label,
				className: group.className,
				icon: group.icon,
				anchor: overrides.length > 0 ? overrides[0].anchor : group.anchor,
				hasOverrides: overrides.length > 0,
				parts: overrides.map((override) => override.label),
				overrides
			};
		})
		.filter((summary) => summary.hasOverrides);
}

function hasNonEmptyObjectValue(value) {
	const parsed = parseMaybeJsonObject(value, {});
	return _.isPlainObject(parsed) && !_.isEmpty(parsed);
}

function hasNonEmptyArrayValue(value) {
	return _.isArray(value) && !_.isEmpty(value);
}

function hasNonEmptyDirectStyleValue(value) {
	if (_.isNil(value)) {
		return false;
	}
	if (_.isString(value)) {
		const normalized = value.trim();
		return normalized !== "" && normalized !== "inherit";
	}
	return value !== false;
}

export function getLocalStylingSummary(entity = {}) {
	const parts = [];

	if (hasNonEmptyObjectValue(entity.styles)) {
		parts.push("Styles");
	}
	if (hasNonEmptyObjectValue(entity.classes)) {
		parts.push("Classes");
	}
	if (hasNonEmptyObjectValue(entity.stylesheet)) {
		parts.push("Stylesheet");
	}
	if (hasNonEmptyObjectValue(entity.theme)) {
		parts.push("Theme");
	}
	if (hasNonEmptyArrayValue(entity.stylesheets)) {
		parts.push("External Stylesheets");
	}

	LOCAL_DIRECT_STYLE_PROPS.forEach((propName) => {
		if (hasNonEmptyDirectStyleValue(entity[propName])) {
			parts.push(_.startCase(propName));
		}
	});

	return {
		hasStyling: parts.length > 0,
		parts
	};
}

export function entityHasLocalStyling(entity = {}) {
	return getLocalStylingSummary(entity).hasStyling;
}

export function resolvePlatformStyle(styles = {}) {
	let result = styles;
	if (_.isPlainObject(result) && Object.prototype.hasOwnProperty.call(result, "default")) {
		if (Object.prototype.hasOwnProperty.call(result, "web")) {
			result = result.web;
		} else if (Object.prototype.hasOwnProperty.call(result, "webOverride")) {
			result = _.assign({}, result.default, result.webOverride);
		} else {
			result = result.default;
		}
	}
	return _.isPlainObject(result) ? result : {};
}

function filterEmptyProps(obj = {}) {
	const result = {};
	_.keys(obj || {}).forEach((key) => {
		const value = obj[key];
		let add = !_.isNull(value) && !_.isUndefined(value);
		if (_.isString(value)) {
			add = add && !_.isEmpty(value);
		}
		if (add) {
			result[key] = value;
		}
	});
	return result;
}

function getEntityFromNode(node) {
	return _.get(node, "entity", node) || {};
}

function getChildrenFromNode(node) {
	return _.isArray(_.get(node, "children")) ? node.children : [];
}

export function normalizeTreePath(treePath = []) {
	const path = _.isArray(treePath) ? treePath.slice() : [];
	if (path[0] === "tree" && path[1] === "root") {
		return path.slice(2);
	}
	if (path[0] === "root") {
		return path.slice(1);
	}
	return path;
}

export function getNodeChainForTreePath(rootNode, treePath = []) {
	const chain = [];
	let currentNode = rootNode;
	if (!currentNode) {
		return chain;
	}
	chain.push(currentNode);

	const path = normalizeTreePath(treePath);
	for (let index = 0; index < path.length; index += 1) {
		if (path[index] !== "children") {
			continue;
		}
		const childIndex = path[index + 1];
		if (!Number.isInteger(childIndex)) {
			break;
		}
		const nextNode = getChildrenFromNode(currentNode)[childIndex];
		if (!nextNode) {
			break;
		}
		currentNode = nextNode;
		chain.push(currentNode);
		index += 1;
	}

	return chain;
}

function getStylePropFromEntity(entity = {}, propNameStyle, defaultResult = null) {
	const styles = parseMaybeJsonObject(entity.styles, {});
	const rawStyle = Object.prototype.hasOwnProperty.call(styles, propNameStyle) ? styles[propNameStyle] : null;
	if (_.isNil(rawStyle) || rawStyle === "") {
		return defaultResult;
	}
	const result = _.isPlainObject(rawStyle) ? rawStyle : parseMaybeJsonObject(rawStyle, defaultResult);
	if (!_.isPlainObject(result) || _.isEmpty(result)) {
		return defaultResult;
	}
	return resolvePlatformStyle(result);
}

function getClassNamesForStyleKeys(entity = {}, propNamesEntity = []) {
	const classes = parseMaybeJsonObject(entity.classes, {});
	const classNames = [];
	const propNames = _.isArray(propNamesEntity) ? propNamesEntity : [propNamesEntity];

	if (_.isString(entity.classes) && entity.classes.trim() !== "" && _.isEmpty(classes)) {
		classNames.push(...entity.classes.replace(/\s+/g, " ").trim().split(" "));
	}

	propNames.forEach((propNameEntity) => {
		const result = Object.prototype.hasOwnProperty.call(classes, propNameEntity) ? classes[propNameEntity] : null;
		if (_.isString(result) && result.trim() !== "") {
			classNames.push(...result.replace(/\s+/g, " ").trim().split(" "));
		} else if (_.isArray(result)) {
			classNames.push(...result);
		}
	});

	return classNames.filter((className) => _.isString(className) && className.trim() !== "");
}

function getStyleAttributeForEntity(entity = {}, overrides = [], propNamesEntity = "styles", defaultResult = {}, activityStylesheet = {}) {
	const propNames = _.isArray(propNamesEntity) ? propNamesEntity : [propNamesEntity];
	const propNamesLastIdx = propNames.length - 1;
	const globalStylesheet = _.isPlainObject(activityStylesheet) ? activityStylesheet : {};
	const deepMergeSequence = [];
	const classNames = getClassNamesForStyleKeys(entity, propNames);
	const classStyles = [];

	classNames.forEach((className) => {
		const dotLeadingClassName = `.${className}`;
		if (Object.prototype.hasOwnProperty.call(globalStylesheet, dotLeadingClassName)) {
			classStyles.push(resolvePlatformStyle(globalStylesheet[dotLeadingClassName]));
		}
	});

	propNames.forEach((propNameEntity, idx) => {
		if (Object.prototype.hasOwnProperty.call(globalStylesheet, propNameEntity)) {
			const resolved = resolvePlatformStyle(globalStylesheet[propNameEntity]);
			deepMergeSequence.push(idx === propNamesLastIdx ? resolved : _.pick(resolved, HIERARCHICAL_STYLE_PROPS));
		}
	});

	if (_.isArray(overrides)) {
		overrides.forEach((override) => {
			if (!_.isPlainObject(override)) {
				return;
			}
			propNames.forEach((propNameStyle, idx) => {
				if (Object.prototype.hasOwnProperty.call(override, propNameStyle)) {
					const resolved = resolvePlatformStyle(override[propNameStyle]);
					deepMergeSequence.push(idx === propNamesLastIdx ? resolved : _.pick(resolved, HIERARCHICAL_STYLE_PROPS));
				}
			});
		});
	}

	if (classStyles.length > 0) {
		deepMergeSequence.push(_.assign({}, ...classStyles));
	}

	if (entity.id && _.isString(entity.id) && entity.id.trim() !== "") {
		const poundLeadingId = `#${entity.id}`;
		if (Object.prototype.hasOwnProperty.call(globalStylesheet, poundLeadingId)) {
			deepMergeSequence.push(resolvePlatformStyle(globalStylesheet[poundLeadingId]));
		}
	}

	propNames.forEach((propNameStyle, idx) => {
		const resolved = getStylePropFromEntity(entity, propNameStyle, defaultResult);
		const result = idx === propNamesLastIdx ? resolved : _.pick(resolved, HIERARCHICAL_STYLE_PROPS);
		if (_.isPlainObject(result) && !_.isEmpty(result)) {
			deepMergeSequence.push(result);
		}
	});

	const finalDeepMergeSequence = deepMergeSequence.filter((styleObject) => _.isPlainObject(styleObject) && !_.isEmpty(styleObject));
	return finalDeepMergeSequence.length > 0 ? deepmerge.all(finalDeepMergeSequence) : defaultResult;
}

export function getDescendantItemStyleOverrides(styleValue = {}) {
	const parsedStyles = parseMaybeJsonObject(styleValue, {});
	const scopedOverrides = _.pick(parsedStyles, DESCENDANT_ITEM_STYLE_OVERRIDE_KEYS);
	return _.isEmpty(scopedOverrides) ? null : scopedOverrides;
}

function buildStyleContextForTreePath(rootNode, treePath = []) {
	const chain = getNodeChainForTreePath(rootNode, treePath);
	const entities = chain.map(getEntityFromNode);
	const activity = entities[0] || {};
	const list = entities.find((entity) => entity.type === "list") || null;
	const section = entities.find((entity) => entity.type === "section") || null;
	const item = entities.find((entity) => _.isString(entity.type) && entity.type.indexOf("item") === 0) || null;

	return {
		activity,
		list,
		section,
		item,
		selectedEntity: entities[entities.length - 1] || activity
	};
}

export function getResolvedItemLabelsStyleForTreePath(rootNode, treePath = []) {
	const { activity, list, section, item } = buildStyleContextForTreePath(rootNode, treePath);
	if (_.isEmpty(activity)) {
		return null;
	}

	const activityStylesheet = parseJsonStyleObject(activity.stylesheet) || {};
	const descendantItemStyleOverrides = [
		getDescendantItemStyleOverrides(activity.styles),
		list ? getDescendantItemStyleOverrides(list.styles) : null,
		section ? getDescendantItemStyleOverrides(section.styles) : null
	].filter((override) => _.isPlainObject(override) && !_.isEmpty(override));
	const targetEntity = item || {};

	let resolvedItem = filterEmptyProps(getStyleAttributeForEntity(targetEntity, descendantItemStyleOverrides, ["activity", "list", "section", "item"], {}, activityStylesheet));
	if (Object.prototype.hasOwnProperty.call(resolvedItem, "lineHeight")) {
		resolvedItem.lineHeight = `${resolvedItem.lineHeight}px`;
	}

	let labels = filterEmptyProps(getStyleAttributeForEntity(targetEntity, descendantItemStyleOverrides, ["labels"], {}, activityStylesheet));
	if (Object.prototype.hasOwnProperty.call(labels, "lineHeight")) {
		labels.lineHeight = `${labels.lineHeight}px`;
	}

	let label1 = filterEmptyProps(getStyleAttributeForEntity(targetEntity, descendantItemStyleOverrides, ["label", "label1"], {}, activityStylesheet));
	if (Object.prototype.hasOwnProperty.call(label1, "lineHeight")) {
		label1.lineHeight = `${label1.lineHeight}px`;
	}

	let label2 = filterEmptyProps(getStyleAttributeForEntity(targetEntity, descendantItemStyleOverrides, ["label", "label2"], {}, activityStylesheet));
	if (Object.prototype.hasOwnProperty.call(label2, "lineHeight")) {
		label2.lineHeight = `${label2.lineHeight}px`;
	}

	label1 = _.assign({}, _.pick(resolvedItem, HIERARCHICAL_STYLE_PROPS), label1);
	label2 = _.assign({}, _.pick(resolvedItem, HIERARCHICAL_STYLE_PROPS), label2);

	if (item) {
		if (item.label1Color && item.label1Color !== "") {
			label1.color = item.label1Color;
		} else if (item.color && item.color !== "") {
			label1.color = item.color;
		}
		if (item.label2Color && item.label2Color !== "") {
			label2.color = item.label2Color;
		}
	}

	return {
		item: resolvedItem,
		labels,
		label1,
		label2
	};
}
